import { useEffect, useMemo } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { textInputClassName } from "@/app/components/routes/auth_routes/QuickCommerce/HomeTileConfig/interface";
import { IGiftFormFields, toSelectItems } from "../interface";
import { FormField, GiftDateField, GiftSelectField, SectionCard, errorBorderClassName } from "./fields";
import { NON_DECIMAL_KEYS, validateNonNegativeDecimal, validatePositiveInteger } from "./validators";

export const GeneralDetailsCard = ({
  allowedStatuses,
  allowedGiftTypes,
  isFormatLoading,
}: {
  allowedStatuses: string[];
  allowedGiftTypes: string[];
  isFormatLoading: boolean;
}) => {
  const { control, getValues, register, trigger } = useFormContext<IGiftFormFields>();
  const { errors } = useFormState({ control });
  const startDate = useWatch({ control, name: "start_date" });

  const statusItems = useMemo(() => toSelectItems(allowedStatuses), [allowedStatuses]);
  const giftTypeItems = useMemo(() => toSelectItems(allowedGiftTypes), [allowedGiftTypes]);

  // Picking a later start date must re-check an end date that is already set.
  useEffect(() => {
    if (startDate && getValues("end_date")) trigger("end_date");
  }, [startDate, getValues, trigger]);

  return (
    <SectionCard title="General Details" description="Identify the gift and set when and how often it can be availed.">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <FormField label="Gift Name" htmlFor="gift_name" required error={errors.gift_name?.message}>
          <input
            id="gift_name"
            type="text"
            placeholder="Enter Gift Name "
            className={`${textInputClassName} ${errorBorderClassName(Boolean(errors.gift_name))}`}
            {...register("gift_name", { validate: (value) => Boolean(value.trim()) || "Gift name is required." })}
          />
        </FormField>
        <FormField label="Gift Tag" htmlFor="gift_tag" required error={errors.gift_tag?.message}>
          <input
            id="gift_tag"
            type="text"
            placeholder="Enter Gift Tag"
            className={`${textInputClassName} ${errorBorderClassName(Boolean(errors.gift_tag))}`}
            {...register("gift_tag", { validate: (value) => Boolean(value.trim()) || "Gift tag is required." })}
          />
        </FormField>
        <GiftSelectField
          name="status"
          label="Status"
          required
          items={statusItems}
          isLoading={isFormatLoading}
          placeholder="Select status"
        />
        <GiftDateField name="start_date" label="Start Date" />
        <GiftDateField
          name="end_date"
          label="End Date"
          minDate={startDate}
          // "YYYY-MM-DD" strings compare in date order. The backend stores both dates at 12:00 AM IST,
          // so the end date must be a later day for the gift to be live at all.
          validate={(value, formValues) =>
            !formValues.start_date ||
            !value ||
            value > formValues.start_date ||
            "End date must be after the start date."
          }
        />
        <FormField label="Limit Per User" htmlFor="limit_per_user" required error={errors.limit_per_user?.message}>
          <input
            id="limit_per_user"
            type="number"
            min={1}
            className={`${textInputClassName} ${errorBorderClassName(Boolean(errors.limit_per_user))}`}
            {...register("limit_per_user", { validate: validatePositiveInteger("Limit per user") })}
          />
        </FormField>
        <FormField
          label="Total Usage Limit"
          htmlFor="total_usage_limit"
          required
          error={errors.total_usage_limit?.message}
        >
          <input
            id="total_usage_limit"
            type="number"
            min={1}
            className={`${textInputClassName} ${errorBorderClassName(Boolean(errors.total_usage_limit))}`}
            {...register("total_usage_limit", { validate: validatePositiveInteger("Total usage limit") })}
          />
        </FormField>
        <GiftSelectField
          name="gift_type"
          label="Gift Type"
          required
          items={giftTypeItems}
          isLoading={isFormatLoading}
          placeholder="Select gift type"
        />
        <FormField
          label="Max Same Gift Types"
          htmlFor="max_same_gift_types"
          required
          error={errors.max_same_gift_types?.message}
        >
          <input
            id="max_same_gift_types"
            type="number"
            min={1}
            className={`${textInputClassName} ${errorBorderClassName(Boolean(errors.max_same_gift_types))}`}
            {...register("max_same_gift_types", { validate: validatePositiveInteger("Max same gift types") })}
          />
        </FormField>
        <FormField label="Gift Priority" htmlFor="gift_priority" required error={errors.gift_priority?.message}>
          <input
            id="gift_priority"
            type="number"
            min={0}
            step="any"
            inputMode="decimal"
            placeholder="e.g. 1 or 1.5"
            onKeyDown={(event) => {
              if (NON_DECIMAL_KEYS.includes(event.key)) event.preventDefault();
            }}
            className={`${textInputClassName} ${errorBorderClassName(Boolean(errors.gift_priority))}`}
            {...register("gift_priority", { validate: validateNonNegativeDecimal("Gift priority") })}
          />
        </FormField>
      </div>
    </SectionCard>
  );
};
